// src/hooks/useConferenciaMutations.ts
import axios from 'axios';
import { apiPost, apiPut, apiDelete } from '../services/api';
import { useApiLock } from './useApiLock';
import { useDragAndDrop } from './useDragAndDrop';
import { showAlert } from '../contexts/DialogContext';
import { Toast } from '../contexts/ToastContext';
import {
  RecebimentoPendenteItem,
  ConferenciaRascunhoItem,
  ConferenciaFinalizadoItem,
} from './useConferenciaKanban';

type Column = 'pendente' | 'rascunho' | 'finalizado';
type DragData = RecebimentoPendenteItem | ConferenciaRascunhoItem | ConferenciaFinalizadoItem;

interface ColumnApi<T> {
  updateItems: (updater: (prev: T[]) => T[]) => void;
  reset: () => void;
}

interface Options {
  pendentes: ColumnApi<RecebimentoPendenteItem>;
  rascunho: ColumnApi<ConferenciaRascunhoItem>;
  finalizados: ColumnApi<ConferenciaFinalizadoItem>;
}

export const useConferenciaMutations = ({
  pendentes,
  rascunho,
  finalizados,
}: Options) => {
  const { isLocked, lock, unlock } = useApiLock();

  const drag = useDragAndDrop<DragData, Column>({
    onDropHandler: async (data, source, target) => {
      if (source === target) return;

      if (source === 'pendente' && target === 'rascunho') {
        const draggedData = data as RecebimentoPendenteItem;
        const key = `/v1/recebimento-conferencia/${draggedData.id}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          const response = await apiPost(`/v1/recebimento-conferencia/${draggedData.id}`, {});
          Toast.success(`Nova conferência nº: ${response.data} criado com sucesso`);
          pendentes.reset();
          rascunho.reset();
        } catch (err) {
          let errorMessage = 'Erro ao criar conferência. Tente novamente.';
          if (axios.isAxiosError(err)) errorMessage = err.response?.data?.error || errorMessage;
          await showAlert(errorMessage, { variant: 'error' });
        } finally {
          unlock(key);
        }
      } else if (source === 'rascunho' && target === 'pendente') {
        const draggedData = data as ConferenciaRascunhoItem;
        const key = `/v1/recebimento-conferencia/${draggedData.id}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          await apiDelete(`/v1/recebimento-conferencia/${draggedData.id}`);
          Toast.success('Conferência excluída com sucesso.');
          rascunho.updateItems((prev) => prev.filter((p) => p.id !== draggedData.id));
          pendentes.reset();
          rascunho.reset();
        } catch (err) {
          let errorMessage = 'Erro ao deletar conferência. Tente novamente.';
          if (axios.isAxiosError(err)) errorMessage = err.response?.data?.error || errorMessage;
          await showAlert(errorMessage, { variant: 'error' });
        } finally {
          unlock(key);
        }
      } else if (source === 'rascunho' && target === 'finalizado') {
        const draggedData = data as ConferenciaRascunhoItem;
        const key = `/v1/recebimento-conferencia/finalizar_${draggedData.id}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          await apiPut(`/v1/recebimento-conferencia/finalizar?recebimentoConferenciaId=${draggedData.id}`);
          Toast.success('Conferência finalizada com sucesso.');
          rascunho.reset();
          finalizados.reset();
        } catch (err) {
          let errorMessage = 'Erro ao finalizar conferência. Tente novamente.';
          if (axios.isAxiosError(err)) errorMessage = err.response?.data?.error || errorMessage;
          await showAlert(errorMessage, { variant: 'error' });
        } finally {
          unlock(key);
        }
      } else if (source === 'finalizado' && target === 'rascunho') {
        const draggedData = data as ConferenciaFinalizadoItem;
        const key = `/v1/recebimento-conferencia/reabrir_${draggedData.id}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          await apiPut(`/v1/recebimento-conferencia/reabrir?recebimentoConferenciaId=${draggedData.id}`);
          Toast.success('Conferência reaberta com sucesso.');
          rascunho.reset();
          finalizados.reset();
        } catch (err) {
          let errorMessage = 'Erro ao reabrir conferência. Tente novamente.';
          if (axios.isAxiosError(err)) errorMessage = err.response?.data?.error || errorMessage;
          await showAlert(errorMessage, { variant: 'error' });
        } finally {
          unlock(key);
        }
      }
      // demais combinações (pendente↔finalizado): no-op
    },
  });

  const onDragStartPendente = (data: RecebimentoPendenteItem) => drag.onDragStart(data, 'pendente');
  const onDragStartRascunho = (data: ConferenciaRascunhoItem) => drag.onDragStart(data, 'rascunho');
  const onDragStartFinalizado = (data: ConferenciaFinalizadoItem) => drag.onDragStart(data, 'finalizado');

  return {
    onDragStartPendente,
    onDragStartRascunho,
    onDragStartFinalizado,
    onDragOver: drag.onDragOver,
    onDrop: drag.onDrop,
  };
};
